import React, { memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Box, IconButton, Typography } from '@mui/material';
import { ArrowIcon, ChatIcon } from '@sharedCR/coreDesignSystem/icons';
import {
  TeamChatChannel,
  TeamChatChannelList,
  TeamChatConnector,
} from 'app/shared/components/TeamChat';

import { IconDirection, IconSize } from 'constants/coreDesignSystem';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';

import { getUuid } from 'store/auth/selectors';
import {
  setBorrowerChatActiveChannelAction,
  setBorrowerChatInitializedAction,
} from 'store/system/actions';
import { selectBorrowerChatActiveChannel } from 'store/system/selectors';
import { fetchChannelsAction } from 'store/teamChat/actions';
import {
  selectTeamChatAllChannels,
  selectTeamChatIsLoadingChannels,
} from 'store/teamChat/selectors';

import {
  channelHeaderNameStyles,
  channelHeaderStyles,
  containerStyles,
  emptyStateStyles,
} from './BorrowerChat.styles';
import { BorrowerChatTestIds } from './BorrowerChat.testIds';

const BorrowerChat = () => {
  const dispatch = useDispatch();

  const loggedUserId = useSelector(getUuid);
  const activeChannelUrl = useSelector(selectBorrowerChatActiveChannel);
  const channels = useSelector(selectTeamChatAllChannels);
  const isLoadingChannels = useSelector(selectTeamChatIsLoadingChannels);

  const onSelectChannel = useCallback(
    chatId => {
      dispatch(setBorrowerChatActiveChannelAction(chatId ?? null));
    },
    [dispatch],
  );

  const onBackToList = useCallback(() => {
    dispatch(setBorrowerChatActiveChannelAction(null));
  }, [dispatch]);

  useEffectSafeStrictMode(() => {
    dispatch(fetchChannelsAction());
    dispatch(setBorrowerChatInitializedAction(true));
  }, [dispatch]);

  if (!loggedUserId) {
    return (
      <Box sx={emptyStateStyles}>
        <ChatIcon size={IconSize[48]} />
        <Typography variant="regular14">Chat unavailable</Typography>
      </Box>
    );
  }

  return (
    <Box sx={containerStyles}>
      <TeamChatConnector>
        {!activeChannelUrl ? (
          <TeamChatChannelList
            activeChannelId={activeChannelUrl}
            channels={channels}
            isLoading={isLoadingChannels}
            onSelectChannel={onSelectChannel}
          />
        ) : (
          <>
            <Box sx={channelHeaderStyles}>
              <IconButton
                data-testid={BorrowerChatTestIds.BACK}
                size="small"
                onClick={onBackToList}
              >
                <ArrowIcon direction={IconDirection.LEFT} size={IconSize[20]} />
              </IconButton>
              <Typography sx={channelHeaderNameStyles} variant="bold14">
                Chat
              </Typography>
            </Box>
            <TeamChatChannel
              isHeaderHidden
              chatId={activeChannelUrl}
              currentUserId={loggedUserId}
              key={activeChannelUrl}
            />
          </>
        )}
      </TeamChatConnector>
    </Box>
  );
};

export default memo(BorrowerChat);
